import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  useRecentCompanies,
  useCompanySearch,
  useCompany,
} from "@/features/companies/api";
import { companyDisplayName } from "@/features/companies/types";
import type { Company } from "@/features/companies/types";

interface Props {
  /** Selected company id, or null when none selected. */
  value: string | null;
  onChange: (companyId: string | null) => void;
  label?: string;
}

export function CompanyPicker({ value, onChange, label }: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { data: selectedCompany } = useCompany(value ?? undefined);
  const { data: recent = [], isLoading: recentLoading } = useRecentCompanies(8);
  const { data: searchResults = [], isLoading: searchLoading } =
    useCompanySearch(query, 15);
  const trimmed = query.trim();
  const results: Company[] = trimmed ? searchResults : recent;

  // Close dropdown on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }
  }, [open]);

  function pickCompany(c: Company) {
    onChange(c.id);
    setQuery("");
    setOpen(false);
  }

  // Selected-state pill view.
  if (value) {
    const displayName = selectedCompany
      ? companyDisplayName(selectedCompany)
      : "Loading…";

    return (
      <div className="space-y-1">
        {label && <Label>{label}</Label>}
        <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{displayName}</p>
            {selectedCompany?.website && (
              <p className="truncate text-xs text-muted-foreground">
                {selectedCompany.website}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            aria-label="Clear company"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Search mode.
  return (
    <div className="relative space-y-1" ref={containerRef}>
      {label && <Label>{label}</Label>}
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            // This search box sits inside deal/contact dialog <form>s — Enter
            // must pick a result, never implicitly submit the form.
            e.preventDefault();
            // Auto-pick only after the user has typed a filter — with an
            // empty query `results` is the recent-companies list, and Enter
            // out of submit habit must not silently link an arbitrary one.
            if (open && trimmed && results.length > 0) pickCompany(results[0]);
          }}
          placeholder="Search companies by name…"
        />
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-popover shadow-lg">
          {!trimmed && (
            <p className="px-3 pt-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Recent
            </p>
          )}
          {(recentLoading || searchLoading) && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              Loading…
            </p>
          )}
          {!recentLoading && !searchLoading && results.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              No companies found.
            </p>
          )}
          <ul>
            {results.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => pickCompany(c)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
                >
                  <div className="font-medium">{companyDisplayName(c)}</div>
                  {(c.website || c.industry) && (
                    <div className="text-xs text-muted-foreground">
                      {[c.industry, c.website].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </button>
              </li>
            ))}
            {!trimmed && results.length === 0 && !recentLoading && (
              <li>
                <p className="px-3 py-2 text-sm text-muted-foreground">
                  No company — link one by searching above.
                </p>
              </li>
            )}
          </ul>
          {value !== null && (
            <div className="border-t">
              <button
                type="button"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent focus:bg-accent focus:outline-none"
              >
                No company
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
